import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [resetSent, setResetSent] = useState(false);

  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const { data, error } = await signIn(email, password);
      if (error) {
        if (error?.message?.toLowerCase().includes('email not confirmed') ||
            error?.message?.toLowerCase().includes('email_not_confirmed')) {
          setError('Please confirm your email before signing in. Check your inbox for the confirmation link.');
        } else if (error?.message?.toLowerCase().includes('invalid login')) {
          setError('Invalid email or password. Please try again.');
        } else {
          setError(error?.message || 'Login failed. Please try again.');
        }
      } else if (data?.user) {
        navigate('/client-management');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://www.nurturebuddy.ai/reset-password',
      });
      if (error) {
        setError(error.message || 'Failed to send reset email. Please try again.');
      } else {
        setResetSent(true);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (mode === 'forgot') {
    return (
      <div className="w-full max-w-md">
        {resetSent ? (
          <div className="text-center space-y-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto" style={{ background: 'rgba(0,180,216,0.12)', border: '1px solid rgba(0,180,216,0.3)' }}>
              <Icon name="Mail" size={24} className="text-primary" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Check your email</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We sent a password reset link to <span className="text-foreground font-medium">{email}</span>.
              It may take a minute to arrive.
            </p>
            <button
              onClick={() => { setMode('login'); setResetSent(false); }}
              className="text-sm text-primary hover:underline font-medium"
            >
              ← Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold text-foreground">Reset your password</h2>
              <p className="text-sm text-muted-foreground">
                Enter your email and we'll send you a reset link.
              </p>
            </div>

            {error && (
              <div className="rounded-lg p-3 text-sm text-destructive" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
                {error}
              </div>
            )}

            <Input
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              placeholder="you@example.com"
              required
              disabled={isLoading}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <><Icon name="Loader2" size={16} className="animate-spin mr-2" />Sending...</>
              ) : 'Send Reset Link'}
            </Button>

            <button
              type="button"
              onClick={() => { setMode('login'); setError(''); }}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-smooth text-center"
            >
              ← Back to sign in
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-md">
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="rounded-lg p-3 text-sm text-destructive" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
            {error}
          </div>
        )}

        <div className="space-y-4">
          <Input
            type="email"
            label="Email Address"
            value={email}
            onChange={(e) => setEmail(e?.target?.value)}
            placeholder="you@example.com"
            required
            disabled={isLoading}
          />

          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={password}
              onChange={(e) => setPassword(e?.target?.value)}
              placeholder="Enter your password"
              required
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
              disabled={isLoading}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
            </button>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => { setMode('forgot'); setError(''); }}
            className="text-sm text-muted-foreground hover:text-primary transition-smooth"
          >
            Forgot password?
          </button>
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <><Icon name="Loader2" size={16} className="animate-spin mr-2" />Signing in...</>
          ) : 'Sign In'}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-sm text-muted-foreground">
          Don't have an account?{' '}
          <Link to="/register" className="text-primary hover:text-primary/80 font-medium">
            Get started free
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
